import { Switch, makeStyles } from '@material-ui/core';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import LockOpenOutlinedIcon from '@material-ui/icons/LockOpenOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import styled, { css } from 'styled-components/macro';
import { ThemeContext } from '../../../contexts/Theme';
import {
  focusRing,
  fontSize,
  fontWeight,
  layout,
  lineHeight,
  media,
  neutral,
  radius,
  space,
  transition,
} from '../../../theme/tokens';

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  max-width: 48rem;
  padding: ${space[3]} 0;
`;

export const Label = styled.label`
  padding: ${space[2]} 0;
  color: ${({ theme }) => theme.headerColour};
  font-weight: ${fontWeight.semibold};
  font-size: ${fontSize.sm};
  line-height: ${lineHeight.tight};
`;

export const Input = styled.input`
  min-height: ${layout.touchTarget};
  padding: ${space[2]} ${space[3]};
  border: 1px solid ${neutral.borderStrong};
  border-radius: ${radius.md};
  background: ${neutral.surface};
  font-family: inherit;
  font-size: ${fontSize.md};
  transition: border-color ${transition.fast};

  &:hover {
    border-color: ${neutral.textMuted};
  }
  &:focus {
    border-color: ${({ theme }) => theme.buttonPrimary};
  }
  ${focusRing}
`;

export const FieldInput = ({ field, form, ...props }) => {
  return <Input {...field} {...props} />;
};

export {
  Button,
  buttonStyles,
  CreateButton,
  DeleteButton,
  SaveChangesButton,
} from './Button';

export const NextArrowForwardIosIcon = styled(ArrowForwardIosIcon)`
  vertical-align: middle;
  padding-left: ${space[2]};
`;

export const FormHeader = styled.h1`
  margin: ${space[5]} 0 ${space[4]};
  font-weight: ${fontWeight.heavy};
  font-size: ${fontSize.xxl};
  line-height: ${lineHeight.tight};
  text-align: center;
  color: ${({ theme }) => theme.headerColour};

  ${media.md`
    font-size: ${fontSize.display};
  `}
`;

export const FormSubHeader = styled.h2`
  margin: ${space[3]};
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.xl};
  line-height: ${lineHeight.tight};
  text-align: center;
  color: ${({ theme }) => theme.headerColour};
`;

export const ScreenWidthMQs = css`
  @media only screen and (min-width: 500px) {
    width: 50%;
    margin: auto;
  }

  @media only screen and (min-width: 900px) {
    width: 30%;
    margin: auto;
  }
`;

export const ModalScreenWidthMQs = css`
  @media only screen and (min-width: 500px) {
    width: 70%;
    margin: auto;
  }

  @media only screen and (min-width: 900px) {
    width: 50%;
    margin: auto;
  }
`;

export const HideScrollBars = css`
  /* IE and Edge */
  -ms-overflow-style: none;
  /* Firefox */
  scrollbar-width: none;
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledAutoSize = styled(TextareaAutosize)`
  width: 100%;
  border: none;
  border-radius: ${radius.sm};
  font-size: ${fontSize.md};
  line-height: ${lineHeight.normal};
  font-family: ${({ theme }) => theme.fontFamily};
  color: ${({ theme }) => theme.textDark};
  &:disabled {
    background-color: ${({ theme }) => theme.appBackground};
  }
`;

export const TextArea = ({ field, form, ...props }) => {
  return <StyledAutoSize {...field} {...props}></StyledAutoSize>;
};

const StyledAssessmentAutoSize = styled(StyledAutoSize)`
  &:disabled {
    background-color: ${({ theme }) => theme.appBackground};
  }
`;

export const AssessmentTextArea = ({ field, form, ...props }) => {
  return (
    <StyledAssessmentAutoSize {...field} {...props}></StyledAssessmentAutoSize>
  );
};

export const FileError = styled.p`
  color: ${({ theme }) => theme.errorMessage};
  font-size: ${fontSize.lg};
  text-align: center;
  font-weight: 600;
  ${({ isClosable }) =>
    isClosable &&
    css`
      margin-top: 0;
    `}
`;

export const InvisibleButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  border-radius: ${radius.md};
  color: inherit;
  &:hover {
    cursor: pointer;
  }
  ${focusRing}
`;

export const LockIcon = (props) => {
  return <LockOutlinedIcon style={{ fontSize: '3em' }} {...props} />;
};

export const UnlockIcon = (props) => {
  return (
    <LockOpenOutlinedIcon
      style={{ fontSize: '3em', color: '#707070' }}
      {...props}
    />
  );
};

export const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${layout.touchTarget};
  margin-top: ${space[2]};
  padding: 0 ${space[3]};
  border: none;
  border-radius: ${radius.md};
  background: none;
  font-weight: ${fontWeight.medium};
  font-size: ${fontSize.sm};
  line-height: ${lineHeight.tight};
  ${({ narrow }) =>
    narrow &&
    css`
      min-height: auto;
    `}
  text-align: center;
  color: ${({ theme }) => theme.headerColour};
  text-decoration-line: underline;
  ${focusRing}
`;

export const LockSwitch = observer(({ ...props }) => {
  const theme = useContext(ThemeContext);
  const classes = makeStyles({
    switchBase: {
      '&$checked': {
        color: theme.lessonHighlight,
      },
      '&$checked + $track': {
        backgroundColor: theme.pendingDark,
      },
    },
    checked: {},
    track: {},
  })();
  return <Switch {...props} classes={classes} />;
});
